import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  FormControlLabel,
  Switch,
  Grid,
  Stack
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, AutoFixHigh as AutoFixHighIcon } from '@mui/icons-material';
import { useAuth } from '../auth';
import { announcementService } from '../service/AnnouncementService';
import {
  Announcement,
  AnnouncementType,
  CreateAnnouncementDto,
  EventDetail,
  RecurringEventDetail,
  RecurringEventOccurrence,
  AnnouncementPrayerRequest
} from '../types/announcement';
import { format } from 'date-fns';
import { baseBackendUrl } from '../service/baseService';

import { useNavigate, useLocation } from 'react-router-dom';
import LocationAutocomplete from './LocationAutocomplete';

const ManageAnnouncements: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { IsAdmin, cookies } = useContext(useAuth);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState<CreateAnnouncementDto>({
    announcementType: AnnouncementType.General,
    title: '',
    description: '',
    publishDate: '',
    expiryDate: '',
    metaData: ''
  });

  // Additional state for specific announcement types
  const [eventDetail, setEventDetail] = useState<EventDetail>({
    announcementId: 0,
    startDateTime: '',
    endDateTime: '',
    location: ''
  });

  const [recurringEventDetail, setRecurringEventDetail] = useState<RecurringEventDetail>({
    announcementId: 0,
    location: '',
    occurrences: []
  });

  const [prayerRequest, setPrayerRequest] = useState<AnnouncementPrayerRequest>({
    announcementId: 0,
    requestorName: '',
    requestDetails: '',
    urgent: false
  });

  const [rewriteDialogOpen, setRewriteDialogOpen] = useState<boolean>(false);
  const [rewrittenDescription, setRewrittenDescription] = useState<string>('');
  const [isRewriting, setIsRewriting] = useState<boolean>(false);

  useEffect(() => {
    if (IsAdmin && cookies?.UserAuth?.token) {
      fetchAnnouncements();
    }
  }, [IsAdmin, cookies]);

  useEffect(() => {
    if (location.state?.createFromPrayerRequest) {
      const { title, description } = location.state.createFromPrayerRequest;
      setFormData(prev => ({
        ...prev,
        announcementType: AnnouncementType.PrayerRequest,
        title,
        description
      }));
      setPrayerRequest(prev => ({
        ...prev,
        requestDetails: description
      }))
      setOpenDialog(true);
      // Clear state so it doesn't reopen on refresh/navigation
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const fetchAnnouncements = async () => {
    if (!cookies?.UserAuth?.token) {
      setError('Authentication token not found');
      return;
    }

    try {
      setLoading(true);
      const data = await announcementService.getAllAnnouncements(cookies.UserAuth.token);
      setAnnouncements(data);
    } catch (err) {
      setError('Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (announcement?: Announcement) => {
    if (announcement) {
      setSelectedAnnouncement(announcement);
      setFormData({
        announcementType: announcement.announcementType,
        title: announcement.title,
        description: announcement.description,
        publishDate: announcement.publishDate ? format(new Date(announcement.publishDate), 'yyyy-MM-dd') : '',
        expiryDate: announcement.expiryDate ? format(new Date(announcement.expiryDate), 'yyyy-MM-dd') : '',
        metaData: announcement.metaData
      });

      // Fetch additional details based on announcement type
      if (cookies?.UserAuth?.token) {
        try {
          switch (announcement.announcementType) {
            case AnnouncementType.Event:
              const eventDetail = await announcementService.getEventDetail(announcement.announcementId, cookies.UserAuth.token);
              setEventDetail(eventDetail);
              break;
            case AnnouncementType.RecurringEvent:
              const occurrences = await announcementService.getRecurringEventOccurrences(announcement.announcementId, cookies.UserAuth.token);
              setRecurringEventDetail({
                announcementId: announcement.announcementId,
                location: (announcement as any).location || '',
                occurrences
              });
              break;
            case AnnouncementType.PrayerRequest:
              // Assuming we have a service method for this
              // const prayerRequest = await announcementService.getPrayerRequest(announcement.announcementId, cookies.UserAuth.token);
              // setPrayerRequest(prayerRequest);
              break;
          }
        } catch (err) {
          setError('Failed to fetch announcement details');
        }
      }
    } else {
      setSelectedAnnouncement(null);
      setFormData({
        announcementType: AnnouncementType.General,
        title: '',
        description: '',
        publishDate: '',
        expiryDate: '',
        metaData: ''
      });
      setEventDetail({ announcementId: 0, startDateTime: '', endDateTime: '', location: '' });
      setRecurringEventDetail({ announcementId: 0, location: '', occurrences: [] });
      setPrayerRequest({ announcementId: 0, requestorName: '', requestDetails: '', urgent: false });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAnnouncement(null);
    setFormData({
      announcementType: AnnouncementType.General,
      title: '',
      description: '',
      publishDate: '',
      expiryDate: '',
      metaData: ''
    });
    setEventDetail({ announcementId: 0, startDateTime: '', endDateTime: '', location: '' });
    setRecurringEventDetail({ announcementId: 0, location: '', occurrences: [] });
    setPrayerRequest({ announcementId: 0, requestorName: '', requestDetails: '', urgent: false });
  };

  const handleSubmit = async () => {
    if (!cookies?.UserAuth?.token) {
      setError('Authentication token not found');
      return;
    }

    try {
      setLoading(true);
      let announcementId: number;

      if (selectedAnnouncement) {
        await announcementService.updateAnnouncement({
          ...formData,
          announcementId: selectedAnnouncement.announcementId
        }, cookies.UserAuth.token);
        announcementId = selectedAnnouncement.announcementId;
        setSuccess('Announcement updated successfully');
      } else {
        // On create, use specific endpoints for Event and RecurringEvent types
        if (formData.announcementType === AnnouncementType.Event) {
          announcementId = await announcementService.createEventAnnouncement({
            title: formData.title,
            description: formData.description,
            publishDate: formData.publishDate || undefined,
            expiryDate: formData.expiryDate || undefined,
            startDateTime: eventDetail.startDateTime,
            endDateTime: eventDetail.endDateTime,
            location: eventDetail.location
          }, cookies.UserAuth.token);
        } else if (formData.announcementType === AnnouncementType.RecurringEvent) {
          announcementId = await announcementService.createRecurringEventAnnouncement({
            title: formData.title,
            description: formData.description,
            publishDate: formData.publishDate || undefined,
            expiryDate: formData.expiryDate || undefined,
            location: recurringEventDetail.location,
            occurrences: recurringEventDetail.occurrences.map(o => ({
              startDateTime: o.startDateTime,
              endDateTime: o.endDateTime,
              metaData: o.metaData || undefined
            }))
          }, cookies.UserAuth.token);
        } else {
          announcementId = await announcementService.createAnnouncement(formData, cookies.UserAuth.token);
        }
        setSuccess('Announcement created successfully');
      }

      // Handle additional data based on announcement type
      if (cookies?.UserAuth?.token) {
        switch (formData.announcementType) {
          case AnnouncementType.Event:
            // For Event type, only update event detail on edit. Creation uses EventAnnouncementRequest endpoint above.
            if (selectedAnnouncement) {
              await announcementService.updateEventDetail({
                ...eventDetail,
                announcementId
              }, cookies.UserAuth.token);
            }
            break;
          case AnnouncementType.RecurringEvent:
            // For RecurringEvent type, only update recurring details on edit. Creation uses RecurringEventAnnouncementRequest endpoint above.
            if (selectedAnnouncement) {
              await announcementService.updateRecurringEventDetail({
                ...recurringEventDetail,
                announcementId
              }, cookies.UserAuth.token);
            }
            break;
          case AnnouncementType.PrayerRequest:
            // Update or create prayer request
            if (selectedAnnouncement) {
              await announcementService.updatePrayerRequest({
                ...prayerRequest,
                announcementId
              }, cookies.UserAuth.token);
            } else {
              await announcementService.createPrayerRequest({
                ...prayerRequest,
                announcementId
              }, cookies.UserAuth.token);
            }
            break;
        }
      }

      fetchAnnouncements();
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save announcement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!cookies?.UserAuth?.token) {
      setError('Authentication token not found');
      return;
    }

    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        setLoading(true);
        await announcementService.deleteAnnouncement(id, cookies.UserAuth.token);
        setSuccess('Announcement deleted successfully');
        fetchAnnouncements();
      } catch (err) {
        setError('Failed to delete announcement');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRewriteDescription = async () => {
    if (!cookies?.UserAuth?.token) {
      setError('Authentication token not found');
      return;
    }

    try {
      setIsRewriting(true);
      const encodedContent = encodeURIComponent(formData.description);
      const response = await fetch(`${baseBackendUrl}/api/email/suggest-rewrite?content=${encodedContent}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${cookies['UserAuth'].token}`
        }
      });

      console.log(response);
      setRewrittenDescription(await response.text());
      setRewriteDialogOpen(true);
    } catch (err) {
      setError('Failed to rewrite description');
    } finally {
      setIsRewriting(false);
    }
  };

  const handleRejectRewrite = () => {
    setRewriteDialogOpen(false);
    setRewrittenDescription('');
  };

  const handleAcceptRewrite = () => {
    if (rewrittenDescription) {
      setFormData({ ...formData, description: rewrittenDescription });
    }
    setRewriteDialogOpen(false);
    setRewrittenDescription('');
  };

  const renderAdditionalFields = () => {
    switch (formData.announcementType) {
      case AnnouncementType.Event:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date & Time"
                type="datetime-local"
                value={eventDetail.startDateTime}
                onChange={(e) => setEventDetail({ ...eventDetail, startDateTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date & Time"
                type="datetime-local"
                value={eventDetail.endDateTime}
                onChange={(e) => setEventDetail({ ...eventDetail, endDateTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <LocationAutocomplete
                value={eventDetail.location}
                onChange={(location) => setEventDetail({ ...eventDetail, location })}
              />
            </Grid>
          </Grid>
        );

      case AnnouncementType.RecurringEvent:
        return (
          <Box>
            <Box sx={{ mb: 2 }}>
              <LocationAutocomplete
                value={recurringEventDetail.location}
                onChange={(location) => setRecurringEventDetail({ ...recurringEventDetail, location })}
              />
            </Box>
            <Typography variant="subtitle1" gutterBottom>
              Event Occurrences
            </Typography>
            {recurringEventDetail.occurrences.map((occurrence, index) => (
              <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="Start Date & Time"
                    type="datetime-local"
                    value={occurrence.startDateTime}
                    onChange={(e) => {
                      const newOccurrences = [...recurringEventDetail.occurrences];
                      newOccurrences[index] = { ...occurrence, startDateTime: e.target.value };
                      setRecurringEventDetail({ ...recurringEventDetail, occurrences: newOccurrences });
                    }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="End Date & Time"
                    type="datetime-local"
                    value={occurrence.endDateTime}
                    onChange={(e) => {
                      const newOccurrences = [...recurringEventDetail.occurrences];
                      newOccurrences[index] = { ...occurrence, endDateTime: e.target.value };
                      setRecurringEventDetail({ ...recurringEventDetail, occurrences: newOccurrences });
                    }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      const newOccurrences = recurringEventDetail.occurrences.filter((_, i) => i !== index);
                      setRecurringEventDetail({ ...recurringEventDetail, occurrences: newOccurrences });
                    }}
                    fullWidth
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
            ))}
            <Button
              variant="outlined"
              onClick={() => {
                setRecurringEventDetail({
                  ...recurringEventDetail,
                  occurrences: [
                    ...recurringEventDetail.occurrences,
                    {
                      occurrenceId: 0,
                      announcementId: 0,
                      startDateTime: '',
                      endDateTime: '',
                      metaData: ''
                    }
                  ]
                });
              }}
            >
              Add Occurrence
            </Button>
          </Box>
        );

      case AnnouncementType.PrayerRequest:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Requestor Name"
                value={prayerRequest.requestorName}
                onChange={(e) => setPrayerRequest({ ...prayerRequest, requestorName: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Request Details"
                value={prayerRequest.requestDetails}
                onChange={(e) => setPrayerRequest({ ...prayerRequest, requestDetails: e.target.value })}
                multiline
                rows={4}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={prayerRequest.urgent}
                    onChange={(e) => setPrayerRequest({ ...prayerRequest, urgent: e.target.checked })}
                  />
                }
                label="Urgent Request"
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  if (!IsAdmin) {
    return (
      <Box sx={{ p: 3, pt: 10, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          Access Denied
        </Typography>
        <Typography>
          You do not have permission to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pt: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Manage Announcements</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          New Announcement
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Publish Date</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {announcements.map((announcement) => (
              <TableRow key={announcement.announcementId}>
                <TableCell>{announcement.title}</TableCell>
                <TableCell>{AnnouncementType[announcement.announcementType]}</TableCell>
                <TableCell>
                  {announcement.publishDate ? format(new Date(announcement.publishDate), 'MM/dd/yyyy') : '-'}
                </TableCell>
                <TableCell>
                  {announcement.expiryDate ? format(new Date(announcement.expiryDate), 'MM/dd/yyyy') : '-'}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(announcement)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(announcement.announcementId)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedAnnouncement ? 'Edit Announcement' : 'New Announcement'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              select
              label="Type"
              value={formData.announcementType}
              onChange={(e) => setFormData({ ...formData, announcementType: Number(e.target.value) })}
              fullWidth
            >
              {Object.entries(AnnouncementType)
                .filter(([key]) => isNaN(Number(key)))
                .map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {key}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
            />
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={4}
                fullWidth
              />
              <Button
                variant="outlined"
                startIcon={<AutoFixHighIcon />}
                onClick={handleRewriteDescription}
                disabled={isRewriting || !formData.description}
                sx={{ minWidth: '120px' }}
              >
                {isRewriting ? 'Rewriting...' : 'AI Rewrite'}
              </Button>
            </Stack>
            <TextField
              label="Publish Date"
              type="date"
              value={formData.publishDate}
              onChange={(e) => setFormData({ ...formData, publishDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Expiry Date"
              type="date"
              value={formData.expiryDate}
              onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            {renderAdditionalFields()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedAnnouncement ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rewriteDialogOpen}
        onClose={handleRejectRewrite}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>AI Rewritten Description</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Original Description:
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {formData.description}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              Rewritten Description:
            </Typography>
            <Typography variant="body1">
              {rewrittenDescription}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectRewrite}>Keep Original</Button>
          <Button onClick={handleAcceptRewrite} variant="contained" color="primary">
            Use Rewritten Version
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageAnnouncements;
